import { getAllBoundariesApi } from 'api-validations/locutus'
import type React from 'react'
import { useEffect } from 'react'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { usePanoramaQuery } from 'shared-ui/api-provider'
import { Loading } from 'shared-ui/components'
import { Pane, planningLayer, useMapAction, useMapContext, useMapState } from 'shared-ui/map'
import { create } from 'zustand'

import { AutoGenerateSelection, BoundaryOptions, Mode } from './PlanningPane/BoundaryOptions'

const usePrescriptionCreatorStarts = create(() => ({ starts: 0 }))

export const startPrescriptionCreatorOver = () =>
  usePrescriptionCreatorStarts.setState(({ starts }) => ({ starts: starts + 1 }))

export const PrescriptionCreatorPane: React.FC = () => {
  const selection = useMapState()
  const starts = usePrescriptionCreatorStarts((state) => state.starts)
  if (selection?.kind !== 'PrescriptionCreator') return null
  return (
    <PrescriptionCreator
      key={`${selection.planningFieldId}:${starts}`}
      planningFieldId={selection.planningFieldId}
    />
  )
}

const PrescriptionCreator: React.FC<{ planningFieldId: string }> = ({ planningFieldId }) => {
  const orgCode = useOrgCodeOrThrow()
  const { map } = useMapContext()
  const mapAction = useMapAction()
  const boundaries = usePanoramaQuery(
    getAllBoundariesApi,
    { orgCode },
    { refetchOnMount: 'always' }
  )
  const defaultBoundary = boundaries.data?.items.find(
    (boundary) => boundary.fieldId === planningFieldId && boundary.isDefault
  )

  useEffect(() => {
    map.drawTool.activate()
    return () => map.drawTool.deactivate()
  }, [map])

  const exit = () => mapAction.mutate({ kind: 'ExitEditor' })

  const showSaved = ([saved]: { id: string }[]) => {
    if (saved === undefined) {
      exit()
      return
    }
    mapAction.mutate({
      kind: 'SelectLayer',
      pane: Pane.Main,
      layer: planningLayer.prescription(planningFieldId, null, saved.id)
    })
  }

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto bg-white px-6 py-5">
      {boundaries.isSuccess && boundaries.isFetchedAfterMount ? (
        <BoundaryOptions
          mode={Mode.Prescription}
          fieldId={planningFieldId}
          boundaryId={defaultBoundary?.id}
          initialAuto={
            defaultBoundary === undefined
              ? AutoGenerateSelection.Unselected
              : AutoGenerateSelection.Auto
          }
          cancel={exit}
          processSavedAssets={showSaved}
        />
      ) : (
        <Loading />
      )}
    </div>
  )
}
